import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import type { PhotoXPosition, PhotoYPosition } from '@graphql/generated-base';
import { EventPageType } from '@graphql/generated-base';
import React, { useEffect, useMemo, useState } from 'react';
import { AuxFrameContainer, AuxFrameInnerContainer } from './AuxFrame.styles';
import type { AuxPhoto } from './AuxFrame.types';
import { zeroRightClassName } from 'react-remove-scroll-bar';
import { usePageRefFromUrl } from '@apps/guest/packages/layout-engine/layouts/LayoutAloha/hooks/usePageRefFromUrl';

interface IAuxFrameContext
  extends Readonly<{
    eventId: string;
    page: Maybe<EventPageType>;
    pageId: string;
    pageSlug?: string;
    photo?: AuxPhoto | null;
    setPageInView: (pageId: string) => void;
    previewFocusPoint: (alignX: PhotoXPosition, alignY: PhotoYPosition) => void;
    reflectPhotoChange: (imageInfo?: AuxPhoto | null) => void;
    pages: ReadonlyArray<EventPageFragment>;
  }> {}

export const AuxFrameContext = React.createContext<IAuxFrameContext>({
  eventId: '',
  page: EventPageType.welcome,
  pageId: '',
  setPageInView: () => {
    throw Error(`No AuxFrameContext`);
  },
  previewFocusPoint: (_alignX, _alignY) => {
    throw Error(`No AuxFrameContext`);
  },
  reflectPhotoChange: () => {
    throw Error(`No AuxFrameContext`);
  },
  pages: [],
});

export const AuxFrameProvider: React.FC<{
  isSmallScreen: boolean;
  eventId: string;
  primaryPhoto?: AuxPhoto | null;
  pages: ReadonlyArray<EventPageFragment>;
  isPreviewing: boolean;
}> = ({ isSmallScreen, eventId, primaryPhoto, pages, children, isPreviewing }) => {
  const [currentPhoto, setCurrentPhoto] = useState<AuxPhoto | undefined | null>(primaryPhoto);
  const [currentPage, setCurrentPage] = useState<EventPageFragment | null | undefined>();
  const [pageInView, setPageInView] = useState<string>();
  const getPageRefFromUrl = usePageRefFromUrl(pages);

  const previewFocusPoint = (alignX: PhotoXPosition, alignY: PhotoYPosition) => {
    if (currentPhoto) {
      setCurrentPhoto({ ...currentPhoto, layout: { alignX, alignY } });
    }
  };

  useEffect(() => {
    const page = pages.find(page => page.id === pageInView);
    if (currentPage?.id !== page?.id) {
      setCurrentPage(page);
    }
  }, [currentPage?.id, pages, pageInView]);

  useEffect(() => {
    if (pages.length) {
      const pageRef = getPageRefFromUrl(pages.map(page => ({ page }))) ?? { page: pages?.[0] };

      if (pageRef) {
        setPageInView(pageRef.page.id);
      }
    }
  }, [pages, getPageRefFromUrl]);

  useEffect(() => {
    if (isSmallScreen) return;

    // Only change photo for desktop when currentPage changes
    setCurrentPhoto(getPhotoToApply());

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryPhoto, pages, currentPage, isSmallScreen]);

  useEffect(() => {
    // Only change photo when a page is changed if (isPreviewing) is active.
    // One of the reasons the page can be changed is if we edit the photo.

    if (!isPreviewing) return;
    setCurrentPhoto(getPhotoToApply());

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages, isPreviewing]);

  const getPhotoToApply = () => {
    // photo will exist coming from the preview listener (angular app publishes data changes)
    // but the url will be null when there isn't a photo for that page
    // the fallback is the event photo, here that is primaryPhoto
    const pageForCurrentPage = (currentPage?.id && pages.find(p => p.id === currentPage.id)) || null;
    const photoToApply = pageForCurrentPage?.photoV2?.url ? pageForCurrentPage?.photoV2 : primaryPhoto;
    return photoToApply;
  };

  const reflectPhotoChange = (imageInfo?: AuxPhoto | null) => {
    if (imageInfo) {
      setCurrentPhoto(imageInfo);
    }
  };

  const welcomePageData = useMemo(() => pages.find(page => page.type === EventPageType.welcome), [pages]);

  return (
    <AuxFrameContext.Provider
      value={{
        eventId,
        page: currentPage?.type || EventPageType.welcome,
        pageSlug: currentPage?.pageSlug,
        pageId: currentPage?.id || welcomePageData?.id || '',
        photo: currentPhoto,
        setPageInView,
        previewFocusPoint,
        reflectPhotoChange,
        pages,
      }}
    >
      {children}
    </AuxFrameContext.Provider>
  );
};

export const AuxFrame: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <AuxFrameContainer className={zeroRightClassName}>
      <AuxFrameInnerContainer>{children}</AuxFrameInnerContainer>
    </AuxFrameContainer>
  );
};
